// src/components/InKindLanding.js
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import inkind_header from "../../images/inkind-header.png";

const InKindLanding = () => {
    const { t, i18n } = useTranslation();
    
    ReactGA.send({
        hitType: "pageview",
        page: window.location.pathname,
        title: t("inkind.meta.title"),
    });

    // Get user info from localStorage
    const userType = localStorage.getItem("type");
    const userCoopType = localStorage.getItem("cooperativeType");

    // Determine user permissions
    const isConsumerCooperative = userType === "cooperative" && userCoopType === "consumer";
    const isServiceProvider = userType === "service_provider";
    const canDonateCrops = !isServiceProvider && !isConsumerCooperative;
    const canDonateInputs = false;
    const canDonateServices = isServiceProvider;
    const canRequestHelp = isConsumerCooperative;

    // Check if user can do anything at all
    const userCanDonate = canDonateCrops || canDonateInputs || canDonateServices;
    const userCanRequest = canRequestHelp;
    const userHasAnyAccess = userCanDonate || userCanRequest;

    // Create contribution types dynamically based on user permissions
    const getContributionTypes = () => {
        const types = [];

        if (canDonateCrops || canRequestHelp) {
            types.push({
                id: 1,
                title: t("inkand.types.crops.title"),
                description: t("inkand.types.crops.description"),
                description_need: t("inkand.types.crops.descriptionNeed"),
                icon: "🌾",
                donateLink: "/home/donate-crop",
                requestLink: "/home/request-crop-help",
                donateText: t("inkand.types.crops.donateText"),
                requestText: t("inkand.types.crops.requestText"),
                canDonate: canDonateCrops,
                canRequest: canRequestHelp,
                donatePermission: t("inkand.types.crops.donatePermission"),
                requestPermission: t("inkand.types.crops.requestPermission"),
                color: "from-green-500 to-emerald-600",
                bgColor: "bg-green-50",
                borderColor: "border-green-200"
            });
        }

        if (canDonateServices || canRequestHelp) {
            types.push({
                id: 3,
                title: t("inkand.types.services.title"),
                description: t("inkand.types.services.description"),
                description_need: t("inkand.types.services.descriptionNeed"),
                icon: "🚜",
                donateLink: "/home/donate-service",
                requestLink: "/home/request-service-help",
                donateText: t("inkand.types.services.donateText"),
                requestText: t("inkand.types.services.requestText"),
                canDonate: canDonateServices,
                canRequest: canRequestHelp,
                donatePermission: t("inkand.types.services.donatePermission"),
                requestPermission: t("inkand.types.services.requestPermission"),
                color: "from-orange-500 to-amber-600",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-200"
            });
        }

        return types;
    };

    const contributionTypes = getContributionTypes();
    
    const stats = [
        { number: "500+", label: t("inkand.stats.contributions") },
        { number: "300+", label: t("inkand.stats.requests") },
        { number: "200+", label: t("inkand.stats.beneficiaries") },
        { number: "50+", label: t("inkand.stats.cooperatives") }
    ];

    // User type display
    const getUserTypeDisplay = () => {
        if (userType === "cooperative") {
            return userCoopType === "consumer" ? t("inkand.userTypes.consumerCooperative") : t("inkand.userTypes.agriculturalCooperative");
        }
        if (userType === "service_provider") return t("inkand.userTypes.serviceProvider");
        if (userType === "farmer") return t("inkand.userTypes.farmer");
        if (userType === "supplier") return t("inkand.userTypes.supplier");
        return t("inkand.userTypes.user");
    };

    // If user has no access at all
    if (!userHasAnyAccess) {
        return (
            <div className="bg-gray-50 w-full flex items-center justify-center" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                <div className="max-w-4xl mx-auto px-4 py-12 text-center">
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12">
                        <div className="text-6xl mb-6">🔒</div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-4">
                            {t("inkand.noAccess.title")}
                        </h1>
                        <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                            {t("inkand.noAccess.description")}
                        </p>
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-2xl mx-auto">
                            <h3 className="font-bold text-yellow-800 mb-2">{t("inkand.noAccess.allowedTypes")}</h3>
                            <ul className="text-yellow-700 text-right space-y-2">
                                <li>{t("inkand.noAccess.farmersSuppliers")}</li>
                                <li>{t("inkand.noAccess.serviceProviders")}</li>
                                <li>{t("inkand.noAccess.consumerCooperatives")}</li>
                                <li>{t("inkand.noAccess.agriculturalCooperatives")}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 w-full" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <Helmet>
                <title>{t("inkand.meta.title")}</title>
                <meta name="description" content={t("inkand.meta.description")} />
                <meta property="og:title" content={t("inkand.meta.ogTitle")} />
                <meta property="og:description" content={t("inkand.meta.ogDescription")} />
            </Helmet>

            {/* User Info Banner */}
            <div className="bg-white border-b border-gray-200 py-4">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex items-center justify-between">
                        <div className="text-right">
                            <h2 className="text-lg font-semibold text-gray-800">
                                {t("inkand.welcome")}
                            </h2>
                            <p className="text-sm text-gray-600">
                                {t("inkand.accountType")}: <span className="font-medium text-primary">{getUserTypeDisplay()}</span>
                            </p>
                        </div>
                        <div className="bg-primary text-white px-3 py-1 rounded-full text-sm">
                            {isConsumerCooperative ? t("inkand.canRequest") :
                                isServiceProvider ? t("inkand.canDonateServices") :
                                t("inkand.canDonateCrops")}
                        </div>
                    </div>
                </div>
            </div>

            {/* Hero Section */}
            <div className="relative min-h-[400px] bg-gradient-to-br from-green-600 to-emerald-700 text-white py-24"
                style={{ backgroundImage: `url(${inkind_header})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0 bg-black opacity-30"></div>
                <div className="relative max-w-7xl mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4">
                        {t("inkand.hero.title")}
                    </h1>
                    <p className="text-xl md:text-2xl mb-8 opacity-90">
                        {t("inkand.hero.subtitle")}
                    </p>
                    <p className="text-lg max-w-3xl mx-auto leading-relaxed">
                        {t("inkand.hero.description")}
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-800 mb-4">
                        {t("inkand.main.howToContribute")}
                    </h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                        {t("inkand.main.selectContributionType")}
                    </p>
                </div>

                {/* If user can only donate or only request, show simplified view */}
                {(!userCanDonate || !userCanRequest) ? (
                    <div className="mb-12">
                        <div className={`text-center mb-8 ${userCanDonate ? 'text-green-600' : 'text-blue-600'}`}>
                            <div className="text-5xl mb-4">
                                {userCanDonate ? '🎁' : '🙏'}
                            </div>
                            <h3 className="text-2xl font-bold mb-2">
                                {userCanDonate
                                    ? t("inkand.main.selectDonationType")
                                    : t("inkand.main.selectRequestType")}
                            </h3>
                        </div>
                    </div>
                ) : null}

                {/* Contribution Types Grid */}
                {contributionTypes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                        {contributionTypes.map((type) => (
                            <div
                                key={type.id}
                                className={`${type.bgColor} rounded-2xl shadow-lg border ${type.borderColor} overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2`}
                            >
                                {/* Header */}
                                <div className={`bg-gradient-to-r ${type.color} p-6 text-center`}>
                                    <div className="text-4xl mb-3">{type.icon}</div>
                                    <h3 className="text-xl font-bold">{type.title}</h3>
                                    {type.canDonate && (
                                        <p className="text-opacity-90 mt-2">{type.description}</p>
                                    )}
                                    {type.canRequest && (
                                        <p className="text-opacity-90 mt-2">{type.description_need}</p>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="p-6">
                                    {type.canDonate && (
                                        <div className={type.canRequest ? "mb-6" : ""}>
                                            <div className="flex items-center mb-3">
                                                <div className="w-3 h-3 bg-green-500 rounded-full mx-2"></div>
                                                <h4 className="font-bold text-gray-800">{t("inkand.actions.wantToDonate")}</h4>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-2">
                                                {t("inkand.actions.haveSurplus")}
                                            </p>
                                            <Link
                                                to={type.donateLink}
                                                className="block w-full bg-green-500 hover:bg-green-600 text-white text-center py-3 px-4 rounded-lg font-bold transition-colors duration-200"
                                                onClick={() => ReactGA.event({
                                                    category: 'In-Kind',
                                                    action: 'Click Donate',
                                                    label: type.title
                                                })}
                                            >
                                                {type.donateText}
                                            </Link>
                                        </div>
                                    )}

                                    {type.canRequest && (
                                        <div>
                                            <div className="flex items-center mb-3">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full mx-2"></div>
                                                <h4 className="font-bold text-gray-800">{t("inkand.actions.readyToRequest")}</h4>
                                            </div>
                                            <p className="text-gray-600 text-sm mb-2">
                                                {t("inkand.actions.requestShare")} {type.title.toLowerCase()}
                                            </p>
                                            <Link
                                                to={type.requestLink}
                                                className="block w-full bg-blue-500 hover:bg-blue-600 text-white text-center py-3 px-4 rounded-lg font-bold transition-colors duration-200"
                                                onClick={() => ReactGA.event({
                                                    category: 'In-Kind',
                                                    action: 'Click Request',
                                                    label: type.title
                                                })}
                                            >
                                                {type.requestText}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-6">😔</div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">
                            {t("inkand.noOptions.title")}
                        </h3>
                        <p className="text-gray-600">
                            {t("inkand.noOptions.description")}
                        </p>
                    </div>
                )}

                {/* My Contributions Management Card */}
                {userHasAnyAccess && (
                    <div className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl shadow-lg overflow-hidden mb-12">
                        <div className="p-8">
                            <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} className="flex flex-col md:flex-row items-center md:justify-start gap-6">
                                <div className="mb-6 md:mb-0 md:ml-6 text-center md:text-right flex-1">
                                    <h3 className="text-2xl font-bold mb-2">{t("inkand.manage.title")}</h3>
                                    <p className="text-opacity-90">
                                        {t("inkand.manage.description")}
                                    </p>
                                </div>

                                <div dir={i18n.language === 'ar' ? 'rtl' : 'ltr'} className="flex flex-col sm:flex-row-reverse gap-4 justify-end">
                                    {userCanDonate && !isServiceProvider && (
                                        <Link
                                            to="/home/profile/crops/my-donations"
                                            className="bg-white text-green-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Donations Button',
                                                label: 'Crops Donations'
                                            })}
                                        >
                                            <span className="ml-2">🎁</span>
                                            {t("inkand.manage.cropsDonations")}
                                        </Link>
                                    )}

                                    {isServiceProvider && (
                                        <Link
                                            to="/home/profile/services/my-donations"
                                            className="bg-white text-orange-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Donations Button',
                                                label: 'Services Donations'
                                            })}
                                        >
                                            <span className="ml-2">🚜</span>
                                            {t("inkand.manage.servicesDonations")}
                                        </Link>
                                    )}

                                    {isConsumerCooperative && (
                                        <Link
                                            to="/home/profile/crops/my-needs"
                                            className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Needs Button',
                                                label: 'Crops Needs'
                                            })}
                                        >
                                            <span className="ml-2">🙏</span>
                                            {t("inkand.manage.cropsRequests")}
                                        </Link>
                                    )}

                                    {isConsumerCooperative && (
                                        <Link
                                            to="/home/profile/services/my-needs"
                                            className="bg-white text-purple-600 hover:bg-gray-100 font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                            onClick={() => ReactGA.event({
                                                category: 'In-Kind',
                                                action: 'Click My Needs Button',
                                                label: 'Services Needs'
                                            })}
                                        >
                                            <span className="ml-2">🚜</span>
                                            {t("inkand.manage.servicesRequests")}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* User Type Info Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">{t("inkand.info.title")}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className={`text-center p-4 rounded-lg border ${(userType === "farmer" || userType === "supplier")
                            ? "bg-green-100 border-green-300"
                            : "bg-green-50 border-green-200"
                            }`}>
                            <div className="text-2xl mb-2">🌾</div>
                            <h4 className="font-bold text-green-800 mb-2">{t("inkand.info.farmers.title")}</h4>
                            <p className="text-sm text-green-700">{t("inkand.info.farmers.description")}</p>
                        </div>
                        <div className={`text-center p-4 rounded-lg border ${userType === "service_provider"
                            ? "bg-blue-100 border-blue-300"
                            : "bg-blue-50 border-blue-200"
                            }`}>
                            <div className="text-2xl mb-2">🚜</div>
                            <h4 className="font-bold text-blue-800 mb-2">{t("inkand.info.serviceProviders.title")}</h4>
                            <p className="text-sm text-blue-700">{t("inkand.info.serviceProviders.description")}</p>
                        </div>
                        <div className={`text-center p-4 rounded-lg border ${isConsumerCooperative
                            ? "bg-purple-100 border-purple-300"
                            : "bg-purple-50 border-purple-200"
                            }`}>
                            <div className="text-2xl mb-2">👥</div>
                            <h4 className="font-bold text-purple-800 mb-2">{t("inkand.info.consumerCooperatives.title")}</h4>
                            <p className="text-sm text-purple-700">{t("inkand.info.consumerCooperatives.description")}</p>
                        </div>
                        <div className={`text-center p-4 rounded-lg border ${userType === "cooperative" && userCoopType === "agriculture"
                            ? "bg-orange-100 border-orange-300"
                            : "bg-orange-50 border-orange-200"
                            }`}>
                            <div className="text-2xl mb-2">🏢</div>
                            <h4 className="font-bold text-orange-800 mb-2">{t("inkand.info.agriculturalCooperatives.title")}</h4>
                            <p className="text-sm text-orange-700">{t("inkand.info.agriculturalCooperatives.description")}</p>
                        </div>
                    </div>
                    <div className="mt-8 p-4 bg-primary bg-opacity-10 border border-primary rounded-lg">
                        <p className="text-center text-primary font-bold">
                            {t("inkand.info.currentUser")}: <span className="text-lg">{getUserTypeDisplay()}</span>
                        </p>
                    </div>
                </div>

                {/* How It Works Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 mb-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t("inkand.howItWorks.title")}</h3>
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-green-100 text-green-600 rounded-full p-2 mx-3 mt-1">
                                        <span className="font-bold">1</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800">{t("inkand.howItWorks.step1.title")}</h4>
                                        <p className="text-gray-600 text-sm">
                                            {t("inkand.howItWorks.step1.description")}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 text-green-600 rounded-full p-2 mx-3 mt-1">
                                        <span className="font-bold">2</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800">{t("inkand.howItWorks.step2.title")}</h4>
                                        <p className="text-gray-600 text-sm">{t("inkand.howItWorks.step2.description")}</p>
                                    </div>
                                </div>
                                <div className="flex items-start">
                                    <div className="bg-green-100 text-green-600 rounded-full p-2 mx-3 mt-1">
                                        <span className="font-bold">3</span>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800">{t("inkand.howItWorks.step3.title")}</h4>
                                        <p className="text-gray-600 text-sm">{t("inkand.howItWorks.step3.description")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                            <h4 className="font-bold text-gray-800 mb-4 text-lg">{t("inkand.importantInfo.title")}</h4>
                            <ul className="space-y-3 text-gray-600 text-sm">
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>{t("inkand.importantInfo.free")}</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>{t("inkand.importantInfo.review")}</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>{t("inkand.importantInfo.delivery")}</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>{t("inkand.importantInfo.privacy")}</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 ml-2">✓</span>
                                    <span>{t("inkand.importantInfo.permissions")}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="text-center bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8">
                    <h3 className="text-2xl font-bold mb-4">{t("inkand.cta.title")}</h3>
                    <p className="text-green-600 mb-6 max-w-2xl mx-auto">
                        {t("inkand.cta.description")}
                    </p>
                    <a href="tel:3623" className="bg-white text-green-600 hover:bg-gray-100 font-bold py-3 px-8 rounded-lg transition-colors duration-200">
                        {t("inkand.cta.button")}
                    </a>
                </div>
            </div>
        </div>
    );
};

export default InKindLanding;